import {
  Button,
  DrawerBody,
  DrawerHeader,
  Heading,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { OnboardingStep } from '@pelicargo/types';
import { useError, useSuccess } from '@pelicargo/ui';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { trpc } from '../../config/trpc';
import { BaseDrawer } from '../BaseDrawer';
import { PermissionForm } from '../Forms/PermissionsForm';
import { UserForm } from '../Forms/UserForm';
import { LabeledText } from '../LabeledText';
import { SimpleForm } from '../SimpleForm';

type FormValues = {
  preferred_name: string;
  full_name: string;
  title: string;
  email: string;
  phone_number: string;
  onboarding_step: OnboardingStep;
  contact_id: number;
  isVerified: boolean;
};

type Props = {
  userId?: number | null;
  isOpen: boolean;
  onClose: () => void;
};

export const UserFormDrawer = ({ userId, isOpen, onClose }: Props) => {
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();
  const methods = useForm();

  const { data } = trpc.getUser.useQuery(
    {
      id: userId as number,
    },
    {
      enabled: !!userId,
    },
  );

  const { mutateAsync: updateUser, isLoading: isUpdating } = trpc.updateUser.useMutation();

  useEffect(() => {
    if (userId) {
      methods.reset({
        preferred_name: data?.preferred_name,
        full_name: data?.full_name,
        email: data?.email,
        phone_number: data?.phone_number,
        title: data?.title,
        preferred_account_id: data?.preferred_account_id,
        onboarding_step: data?.onboarding_step,
        isVerified: !!data?.email_verified_at,
        contact_id: data?.contact_id,
      });
    }
  }, [data, userId, methods]);

  const onValid = useCallback(
    async (values: FormValues) => {
      try {
        if (userId) {
          await updateUser({
            id: userId,
            ...values,
            email_verified_at: values.isVerified ? new Date() : null,
          });
        }
        onClose();
        handleSuccess('User updated');
      } catch (error) {
        handleError(error);
      }
    },
    [userId, onClose, handleSuccess, updateUser, handleError],
  );

  const handleSubmit = methods.handleSubmit(onValid);

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <BaseDrawer isOpen={isOpen} onClose={handleClose}>
      <DrawerHeader>
        <Heading variant="h4">Update User</Heading>
      </DrawerHeader>
      <DrawerBody>
        <Tabs>
          <TabList>
            <Tab>Information</Tab>
            <Tab>Permissions</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <SimpleForm {...methods} submitOnBlur={false}>
                <Stack spacing="4">
                  <UserForm userId={userId} onClose={handleClose} />
                  <LabeledText label="Verification Code" value={data?.verification_code?.code ?? '-'} />
                  <Stack direction="row" justify="space-between" align="center" w="full" spacing="2">
                    <Text>ID: {userId}</Text>
                    <Button type="submit" colorScheme="brand" isLoading={isUpdating} onClick={handleSubmit}>
                      Update User
                    </Button>
                  </Stack>
                </Stack>
              </SimpleForm>
            </TabPanel>
            <TabPanel>
              <PermissionForm userId={userId} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </DrawerBody>
    </BaseDrawer>
  );
};
